import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { clampString, cn } from '@/lib/utils';
import { CreateWorkspaceModal } from '@/views/settings/create-workspace-modal';
import NewMemberModal from '@/views/settings/team/components/new-add-member-modal';
import { IOption } from '@/views/home/dashboard/components/datasource-dropdown';
import { Business } from '@sparrow-marketing/contracts';
import {
  ClientContainer,
  TeamContainer,
  UserContainer,
  ReportsContainer,
} from '@/providers';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ChartsState, SelectedInsightsState, useInsights } from '@/states';
import { LOCAL_STORAGE_TYPES, APP_ROUTE, COOKIE_KEYS } from '@/constants';
import useLocalStorage from '@/hooks/localstorage';
import {
  FORBIDDEN_QUERY_PARAMS_ROUTES,
  QUERY_PARAMS,
  parseQueryParameters,
  getQueryParameters,
  mergeParameters,
} from '@/helpers/query-params-utils';
import { useEffect, useMemo } from 'react';
import TeamUpgradeDialog from '@/views/settings/team/components/team-upgrade-dialog';
import { useGetMembers } from '@/hooks/api/team';
import { useTranslation } from 'react-i18next';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRefreshSession, useSession } from '@/states/common';
import { getSubscriptionName } from '@/views/settings/billing/utils';
import ContactSupportModal from '@/views/settings/team/components/contact-support-modal';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import useBilling from '@/hooks/use-billing';

const { CLIENT } = QUERY_PARAMS;

interface Workspace {
  id: number;
  name: string;
  logo?: string;
  members?: number;
  verified?: boolean;
  workspaceId?: number;
  value: string | number;
  disabled?: boolean;
  accounts?: Array<any>;
}

export function WorkspaceSwitcher({ workspaces }: { workspaces: any }) {
  const { t } = useTranslation('workspace');
  const sidebar = useSidebar();
  const [activeTeam, setActiveTeam] = React.useState(workspaces[0]);
  const queryClient = useQueryClient();
  const [
    showCreateWorkspaceModal,
    setShowCreateWorkspaceModal,
  ] = React.useState(false);
  const [showInviteMembersModal, setShowInviteMembersModal] = React.useState(
    false
  );
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [upgradeResourceType, setUpgradeResourceType] = React.useState<
    'workspaces' | 'users'
  >('users');
  const [isLoadingWorkspace, setIsLoadingWorkspace] = React.useState(false);
  const navigate = useNavigate();
  const { state: userState } = UserContainer.useContainer();
  const {
    state: clientState,
    updateSelectedClient,
    updatingSelectedClient,
  } = ClientContainer.useContainer();
  const { state: teamState } = TeamContainer.useContainer();
  const teamAmount = teamState?.members?.length - 1;

  const { clients } = clientState;
  const { get, set } = useLocalStorage();
  const localStorageClient = get(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {};
  const setSelectedInsightsState = useSetRecoilState(SelectedInsightsState);
  const resetChart = useResetRecoilState(ChartsState);
  const { clearInsights } = useInsights();
  const { resetReportsState } = ReportsContainer.useContainer();
  const { data: session } = useSession();
  const { permission, billing: billingInfo } = session;
  const location = useLocation();
  const refreshSession = useRefreshSession();
  const getMembers = useGetMembers();
  const businessId = userState?.business?.id;
  const isLegacyUser = userState?.business?.billing?.isLegacyUser;
  const [showContactSalesModal, setShowContactSalesModal] = React.useState(
    false
  );
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const {
    isAppSumoCustomer: isAppSumo,
    isTenCodePurchaser: isTenCode,
  } = useBilling();

  useEffect(() => {
    if (businessId) {
      getMembers(businessId);
    }
  }, [businessId]);

  const { limitReached } = usePlanLimits();
  const isLimitMembers = limitReached.users;
  const isWorkspaceNotAllowed = limitReached.workspaces;

  const userRole = permission?.role ?? 'member';

  const isMac = useMemo(() => {
    return navigator.platform.toLowerCase().includes('mac');
  }, []);

  const handleAccountLinkClick = async (
    workspace: Workspace
  ): Promise<void> => {
    // Show loader
    setIsLoadingWorkspace(true);

    // Track the workspace switch event immediately
    trackEvent('navigation workspace tab', {
      clientId: clientState.selectedClient?.id,
      clientName: clientState.selectedClient?.name,
      businessId: clientState.selectedClient?.businessId,
      id: workspace.id,
      name: workspace.name,
    });

    try {
      // Clear state immediately
      setSelectedInsightsState({});
      clearInsights();
      resetChart();
      resetReportsState();

      const clientId = workspace.id;

      if (clientId !== clientState.selectedClient?.id) {
        updatingSelectedClient();
        updateSelectedClient(clientId);

        // Invalidate client-specific queries without auto-refetching
        queryClient.invalidateQueries({
          queryKey: ['insights'],
          refetchType: 'none',
        });
      }

      // Check if current path includes analytics and redirect to overview
      const isAnalyticsRoute = location.pathname.includes(
        `/${APP_ROUTE.ANALYTICS}`
      );
      const newPath = isAnalyticsRoute
        ? `/ws/${clientId}/${APP_ROUTE.ANALYTICS}`
        : location.pathname.replace(/\/ws\/[^/]+/, `/ws/${clientId}`);

      navigate(newPath);

      // Wait for session refresh to complete
      await refreshSession();
    } finally {
      setIsLoadingWorkspace(false);
    }
  };

  const hotkeyCombinations = clients
    .map((_, index: number) => `alt+${index + 1}`)
    .join(',');

  useHotkeys(
    hotkeyCombinations,
    event => {
      event.preventDefault();
      const clientIndex = parseInt(event.code.slice(-1), 10) - 1;
      if (clientIndex >= 0 && clientIndex < clients.length) {
        handleAccountLinkClick(clients[clientIndex]);
      }
    },
    {
      preventDefault: true,
    },
    [clients, handleAccountLinkClick]
  );

  return (
    <div>
      {isLoadingWorkspace && (
        <BusinessSwitchLoader
          message={t('switching-workspace', 'Switching workspace...')}
        />
      )}
      <SidebarMenu>
        <SidebarMenuItem className={cn(!sidebar.open && 'mx-auto')}>
          {sidebar.open ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {/* Workspace Switcher */}
              <DropdownMenu
                open={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
              >
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button className="w-full px-2.5 py-2 flex items-center gap-2.5 hover:bg-gray-50/80 transition-all group rounded-md">
                          <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0 ring-1 ring-blue-100/50">
                            {clientState.selectedClient?.logo ? (
                              <img
                                src={clientState.selectedClient?.logo}
                                alt={clientState.selectedClient?.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <i className="ti ti-building text-blue-600 text-base" />
                            )}
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-semibold text-[13px] text-gray-900 group-hover:text-gray-950 truncate leading-tight">
                              {clientState.selectedClient?.name}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate font-medium mt-0.5">
                              {clientState.selectedClient?.accounts?.length ||
                                0}{' '}
                              {clientState.selectedClient?.accounts?.length !==
                              1
                                ? t('sources', 'sources')
                                : t('source', 'source')}
                            </div>
                          </div>
                          <i className="ti ti-selector text-gray-400 group-hover:text-gray-600 text-xs flex-shrink-0 transition-all" />
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold">
                          {clientState.selectedClient?.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {t(
                            'click-to-switch-workspace',
                            'Click to switch workspace'
                          )}
                        </span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DropdownMenuContent
                  className="w-[240px] rounded-xl font-sans p-1.5 shadow-lg border-gray-100"
                  align="start"
                  side={sidebar.isMobile ? 'bottom' : 'bottom'}
                  sideOffset={8}
                >
                  <div className="px-2.5 py-1.5 mb-0.5">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {t('workspaces', 'Workspaces')}
                    </span>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {clients?.map((workspace: Workspace, index: number) => (
                      <DropdownMenuItem
                        key={workspace.id}
                        onClick={() => handleAccountLinkClick(workspace)}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                      >
                        <div className="flex items-center gap-2.5 w-full">
                          <div className="flex aspect-square size-7 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0 ring-1 ring-blue-100/50">
                            {workspace.logo ? (
                              <img
                                src={workspace.logo}
                                alt={workspace.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <i className="ti ti-building text-blue-600 text-sm" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[13px] font-semibold text-gray-900 truncate">
                                {workspace.name}
                              </span>
                              {workspace.verified && (
                                <i className="ti ti-circle-check text-blue-500 text-xs" />
                              )}
                            </div>
                            <span className="text-[11px] text-gray-500 font-medium">
                              {workspace?.accounts?.length || 0}{' '}
                              {workspace?.accounts?.length !== 1
                                ? t('sources', 'sources')
                                : t('source', 'source')}
                            </span>
                          </div>
                          {clientState.selectedClient?.id === workspace.id ? (
                            <i className="ti ti-check text-primary text-base flex-shrink-0" />
                          ) : (
                            <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 border border-gray-200 px-1 font-mono text-[10px] text-gray-400 bg-white rounded flex-shrink-0">
                              <span>{isMac ? '⌥' : 'Alt'}</span>
                              <span>{index + 1}</span>
                            </kbd>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>

                  <DropdownMenuSeparator className="my-1.5 bg-gray-100" />

                  {userRole !== 'member' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          navigate(
                            `${APP_ROUTE.SETTINGS}/${APP_ROUTE.WORKSPACE}`
                          );
                          setIsDropdownOpen(false);
                        }}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                      >
                        <i className="ti ti-settings text-gray-500 mr-2.5 text-sm" />
                        <span className="text-[13px] font-medium text-gray-700">
                          {t('workspace_settings', 'Workspace settings')}
                        </span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => {
                          if (isLimitMembers) {
                            if (isAppSumo || isTenCode) {
                              setShowContactSalesModal(true);
                            } else {
                              setUpgradeResourceType('users');
                              setShowUpgradeModal(true);
                            }
                          } else {
                            setShowInviteMembersModal(true);
                          }
                          setIsDropdownOpen(false);
                        }}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                      >
                        <i className="ti ti-user-plus text-gray-500 mr-2.5 text-sm" />
                        <span className="text-[13px] font-medium text-gray-700">
                          {t('invite_members', 'Invite Members')}
                        </span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => {
                          if (isWorkspaceNotAllowed) {
                            setUpgradeResourceType('workspaces');
                            setShowUpgradeModal(true);
                          } else {
                            setShowCreateWorkspaceModal(true);
                          }
                          setIsDropdownOpen(false);
                        }}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                      >
                        <i className="ti ti-plus text-gray-500 mr-2.5 text-sm" />
                        <span className="text-[13px] font-medium text-gray-700">
                          {t('create_workspace', 'Create Workspace')}
                        </span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className={cn(
                    !sidebar.open &&
                      'mt-4 justify-center group-data-[collapsible=icon]:!size-11 bg-white border border-gray-200 rounded-lg'
                  )}
                >
                  {clientState.selectedClient?.logo ? (
                    <img
                      src={clientState.selectedClient?.logo}
                      alt={clientState.selectedClient?.name}
                      className="w-7 h-7 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="bg-gradient-to-r from-blue-500 to-blue-300 p-4 rounded-lg">
                      <i className="ti ti-building text-gray-50" />
                    </div>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[240px] rounded-xl font-sans p-1.5 shadow-lg border-gray-100"
                align="start"
                side={sidebar.isMobile ? 'bottom' : 'bottom'}
                sideOffset={8}
              >
                <div className="px-2.5 py-1.5 mb-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    {t('workspaces', 'Workspaces')}
                  </span>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {clients?.map((workspace: Workspace, index: number) => (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => handleAccountLinkClick(workspace)}
                      className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                    >
                      <div className="flex items-center gap-2.5 w-full">
                        <div className="flex aspect-square size-7 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0 ring-1 ring-blue-100/50">
                          {workspace.logo ? (
                            <img
                              src={workspace.logo}
                              alt={workspace.name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <i className="ti ti-building text-blue-600 text-sm" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-semibold text-gray-900 truncate">
                              {workspace.name}
                            </span>
                            {workspace.verified && (
                              <i className="ti ti-circle-check text-blue-500 text-xs" />
                            )}
                          </div>
                          <span className="text-[11px] text-gray-500 font-medium">
                            {workspace?.accounts?.length || 0}{' '}
                            {workspace?.accounts?.length !== 1
                              ? t('sources', 'sources')
                              : t('source', 'source')}
                          </span>
                        </div>
                        {clientState.selectedClient?.id === workspace.id ? (
                          <i className="ti ti-check text-primary text-base flex-shrink-0" />
                        ) : (
                          <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 border border-gray-200 px-1 font-mono text-[10px] text-gray-400 bg-white rounded flex-shrink-0">
                            <span>{isMac ? '⌥' : 'Alt'}</span>
                            <span>{index + 1}</span>
                          </kbd>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </SidebarMenuItem>
      </SidebarMenu>
      <CreateWorkspaceModal
        open={showCreateWorkspaceModal}
        onOpenChange={setShowCreateWorkspaceModal}
        onComplete={() => {
          setShowCreateWorkspaceModal(false);
          setIsDropdownOpen(false);
        }}
      />
      <NewMemberModal
        isOpen={showInviteMembersModal}
        onClose={() => setShowInviteMembersModal(false)}
      />
      <TeamUpgradeDialog
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        resourceType={upgradeResourceType}
      />
      <ContactSupportModal
        open={showContactSalesModal}
        onOpenChange={setShowContactSalesModal}
      />
    </div>
  );
}
